import { LoginDto, RegisterDto, SignupDto, ResetPasswordDto, ForgotUsernameDto, VerifyCodeDto, UpdatePasswordDto, UpdateUserDto } from '../infrastructure/dto/AuthDto';

import { AuthUseCase } from '../domain/usecases/AuthUseCase';
import { LoginValues } from '../infrastructure/StorageService';
import { UserCredentialsService } from './UserCredentialsService';
import { NavigateFunction } from 'react-router-dom';

/**
 * Application service that handles authentication requests
 * Coordinates between domain layer and UI
 */
export class AuthService {
  constructor(
    private readonly authUseCase: AuthUseCase,
    private readonly userCredentialsService: UserCredentialsService,
  ) {}

  /**
   * Save the current path as returnTo URL
   */
  saveReturnToUrl(): void {
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('returnTo', currentPath);
  }

  /**
   * Get and clear the returnTo URL
   */
  getAndClearReturnToUrl(): string | null {
    const returnTo = sessionStorage.getItem('returnTo');
    sessionStorage.removeItem('returnTo');
    return returnTo;
  }

  /**
   * Login with credentials
   */
  async login(params: LoginDto, navigate: NavigateFunction): Promise<void> {
    const { userName } = params;

    // Call the domain use case
    await this.authUseCase.login(params);

    // Save user credentials
    this.userCredentialsService.saveUserCredentials(userName);

    // Check if there's a returnTo URL
    const returnTo = this.getAndClearReturnToUrl();
    if (returnTo) {
      navigate(returnTo);
    } else {
      // Default redirect if no returnTo is present
      navigate('/home');
    }
  }

  /**
   * Register with credentials
   */
  async register(params: RegisterDto): Promise<void> {
    return this.authUseCase.register(params);
  }

  /**
   * Signup without activation code
   */
  async signup(params: SignupDto): Promise<void> {
    return this.authUseCase.signup(params);
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await this.authUseCase.logout();
  }

  /**
   * Reset password
   */
  async resetPassword(params: ResetPasswordDto): Promise<void> {
    return this.authUseCase.resetPassword(params);
  }

  /**
   * Forgot username
   */
  async forgotUsername(params: ForgotUsernameDto): Promise<void> {
    return this.authUseCase.forgotUsername(params);
  }

  /**
   * Verify code
   */
  async verifyCode(params: VerifyCodeDto): Promise<number> {
    return this.authUseCase.verifyCode(params);
  }

  /**
   * Update password
   */
  async updatePassword(params: UpdatePasswordDto): Promise<void> {
    return this.authUseCase.updatePassword(params);
  }

  /**
   * Update user profile
   */
  async updateUser(params: UpdateUserDto): Promise<void> {
    return this.authUseCase.updateUser(params);
  }

  /**
   * Verify token
   */
  verifyToken(token: string): boolean {
    return this.authUseCase.verifyToken(token);
  }

  /**
   * Get saved credentials
   */
  getSavedCredentials(): LoginValues {
    return this.userCredentialsService.getSavedCredentials();
  }
}
